import React, { useCallback } from 'react';

import { ApolloError } from '@apollo/client';
import { Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import Link from 'components/link';
import {
  CONTACT_US_ROUTE_KEY,
  getErrorDialogOptions,
  getOrderFailureMessageKey,
} from 'experiments/checkout-v2/control/pages/cart/payment/get-error-dialog-options';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { ModalCb } from 'hooks/use-error-modal';
import { routes } from 'navigation/routing';
import { GraphQLErrorCodes, isPspRefusedError, parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLError } from 'utils/network';

export interface IHandleCommitErrorInput {
  commitError: ApolloError | GraphQLError | undefined;
  dismissErrorDialog: () => void;
  errorMsgId?: string;
  onCoolingPeriodConfirm: () => void;
  openErrorDialog: ModalCb;
  confirmationTextId?: 'placeOrder' | 'continue';
  fdAccountId: string;
}

export const useHandleCommitError = ({
  commitError,
  dismissErrorDialog,
  errorMsgId,
  onCoolingPeriodConfirm,
  openErrorDialog,
  confirmationTextId = 'placeOrder',
  fdAccountId,
}: IHandleCommitErrorInput) => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const handleCommitError = useCallback(
    (error: ApolloError | GraphQLError) => {
      if (isPspRefusedError(error)) {
        return;
      }

      let errorDialog;
      const errorCode = parseGraphQLErrorCodes(error)[0]?.errorCode;

      if (errorCode === GraphQLErrorCodes.COMMIT_ORDER_COOLING_PERIOD) {
        errorDialog = {
          body: formatMessage({ id: 'errorDialogCooldownMessage' }),
          title: formatMessage({ id: 'errorDialogCooldownTitle' }),
          message: formatMessage({ id: 'errorDialogCooldownMessage' }),
          dismissText: formatMessage({ id: 'viewRecentOrders' }),
          confirmationText: formatMessage({ id: confirmationTextId }),
          onDismiss: () => router.replace(routes.orders),
          onConfirm: () => {
            dismissErrorDialog();
            onCoolingPeriodConfirm();
          },
        };
      } else {
        const errorMessageId = errorMsgId || getOrderFailureMessageKey(fdAccountId, error);
        const body = (
          <Text>
            {formatMessage(
              { id: errorMessageId },
              {
                link: (
                  <Link
                    isInline
                    isUnderlined
                    to={CONTACT_US_ROUTE_KEY}
                    onPress={dismissErrorDialog}
                  >
                    {formatMessage({ id: 'here' })}
                  </Link>
                ),
              }
            )}
          </Text>
        );
        errorDialog = getErrorDialogOptions({
          body,
          error,
          formatMessage,
          errorMsgId: errorMessageId,
        });
      }

      openErrorDialog(errorDialog);
    },
    [
      openErrorDialog,
      formatMessage,
      confirmationTextId,
      router,
      dismissErrorDialog,
      onCoolingPeriodConfirm,
      errorMsgId,
      fdAccountId,
    ]
  );

  useEffectOnUpdates(() => {
    if (commitError) {
      handleCommitError(commitError);
    }
  }, [commitError]);

  return { handleCommitError };
};
